import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { chatQueryKeys, getChatMessages, MESSAGE_PAGE_SIZE } from 'services/api/chat';

/**
 * A conversation's messages as an upward-scrolling infinite list (OLIO-2833).
 *
 * Unlike every existing infinite query in this repo (page-number, downward scroll), chat
 * is cursor-based: `getNextPageParam` derives the composite `cursor.lt` anchor
 * (`<createdAt>|<id>`) from the OLDEST loaded message, and a short page ends history. The server returns each page newest-first
 * (created_at DESC); we flatten the pages and reverse so the list reads oldest→newest
 * (newest pinned to the bottom). Scroll anchoring lives in MessageList.
 */
export default function useChatMessages(conversationId: string) {
  const query = useInfiniteQuery({
    queryKey: chatQueryKeys.messages(conversationId),
    queryFn: ({ pageParam, signal }) =>
      getChatMessages(conversationId, { before: pageParam, pageSize: MESSAGE_PAGE_SIZE }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      // A short page means we've reached the start of the conversation — stop.
      if (lastPage.data.length < MESSAGE_PAGE_SIZE) return undefined;
      // Anchor the next "load older" on the oldest loaded message's position: createdAt
      // alone can't mark the boundary within a same-timestamp run — the id completes it.
      const oldest = lastPage.data[lastPage.data.length - 1];
      return oldest ? `${oldest.createdAt}|${oldest.id}` : undefined;
    },
    enabled: !!conversationId,
  });

  const messages = useMemo(() => (query.data?.pages ?? []).flatMap((page) => page.data).reverse(), [query.data]);

  return {
    messages,
    isLoading: query.isPending,
    isError: query.isError,
    hasOlder: query.hasNextPage,
    isFetchingOlder: query.isFetchingNextPage,
    fetchOlder: query.fetchNextPage,
  };
}
